import { useTranslation } from "react-i18next";
import { FileCheck2, FileX2, Users } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export interface QuoteScopeCardProps {
  /** The discipline of the leg this professional is quoting. */
  myDiscipline: "notary" | "accountant";
  /** Other disciplines quoting the same request. Empty on a single-discipline job. */
  otherDisciplines: Array<"notary" | "accountant">;
  /** The intake's explanation of how the work splits. */
  scopeRationale: string | null;
  anticipatedDocuments: Array<{
    label: string;
    /** 'text' items were answered by typing — look in `intakeDetails`, not the files. */
    kind?: "file" | "text";
    documentType: string | null;
    why: string;
  }>;
  /** Document types the consumer actually attached, for marking slots filled. */
  providedDocumentTypes: string[];
  /** What the consumer typed into the text items, keyed by the item's label. */
  intakeDetails?: Array<{ label: string; value: string }>;
}

/**
 * What the professional is actually being asked to price (spec ce646966
 * follow-up, from a screenshot review).
 *
 * The page used to give a professional a title, a free-form description and a
 * price field. On a two-discipline job that is actively misleading: both pools
 * receive the SAME description, so an escribano reads "necesito el alta en DGI
 * y BPS con la puesta en marcha contable" with nothing saying a contador is
 * separately quoting exactly that. They price work they will never do, or skip
 * a job they would have won. Everything this card shows was already stored and
 * shown to nobody.
 */
export function QuoteScopeCard({
  myDiscipline,
  otherDisciplines,
  scopeRationale,
  anticipatedDocuments,
  providedDocumentTypes,
  intakeDetails = [],
}: QuoteScopeCardProps): JSX.Element | null {
  const { t } = useTranslation();

  const isShared = otherDisciplines.length > 0;
  if (!isShared && !scopeRationale && anticipatedDocuments.length === 0) return null;

  const otherLabels = otherDisciplines.map((d) =>
    t(`requests.user.quoteDisciplineArticle_${d}`),
  );
  const provided = new Set(providedDocumentTypes);
  // Answers are keyed by the item's label, which is the only thing that pairs
  // a typed value back to the question that asked for it.
  const answerByLabel = new Map(intakeDetails.map((detail) => [detail.label, detail.value]));
  // "Documentos previstos" stops being accurate once one of the items is a
  // number the consumer typed.
  const scopeItemsLabelKey = anticipatedDocuments.some((doc) => doc.kind === "text")
    ? "requests.scope.itemsLabel"
    : "requests.scope.documentsLabel";

  return (
    // The accent lives on the CARD, not on a tinted box inside it. This card
    // held a bordered, tinted callout as its first child, so the one sentence
    // that matters most on the page — which half of the job you are pricing —
    // arrived wrapped in two borders. Same emphasis, one layer.
    // An ACCENT BAR, not a fill. This was a tinted slab spanning the page with
    // all of its text in the left third, and the tint is what made the empty
    // two-thirds read as a design mistake rather than as whitespace. A 3px
    // left edge carries the same "this one matters" signal at a fraction of
    // the visual weight, and an untinted card stops competing with the ones
    // below it.
    <Card
      className={
        isShared ? "border-l-[3px] border-l-primary shadow-sm" : "shadow-sm"
      }
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("requests.scope.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* One block, not two. The callout and the rationale were both
            announcing "this job needs two disciplines" — on top of the
            description above already saying it — so the reader met the same
            fact three times and had to work out whether they differed. The
            callout keeps the actionable half (what NOT to price); the
            rationale sits under it as the specific detail, unlabelled,
            because the box it is in already says what it is. */}
        {isShared ? (
          // The icon sat beside the SENTENCE, which made a two-line heading
          // hang off a 16px glyph. It belongs with the label above instead, so
          // the sentence can start at the card's own left edge and read as the
          // headline it is.
          //
          // max-w caps the MEASURE: the card stretches to the page width, so
          // at desktop size this ran edge to edge as a single line — the one
          // explanation a professional must actually read was the hardest
          // thing on the page to read.
          <div className="min-w-0 max-w-[72ch] space-y-2">
            <p className="flex items-start gap-2 text-base font-semibold leading-6 text-foreground">
              <Users className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>
                {t("requests.scope.sharedTitle", {
                  mine: t(`requests.scope.part_${myDiscipline}`),
                  others: otherLabels.join(t("requests.user.quoteDisciplineJoin")),
                })}
              </span>
            </p>
            {scopeRationale ? (
              <p className="whitespace-pre-line break-words pl-6 text-sm leading-relaxed text-muted-foreground">
                {scopeRationale}
              </p>
            ) : null}
          </div>
        ) : scopeRationale ? (
          // UNLABELLED, for the same reason the shared branch above is: the
          // card header already says "Qué se te pide" and this sentence is the
          // answer to it.
          //
          // It used to carry its own "Cómo se reparte el trabajo" label,
          // styled identically to the card header — same size, same uppercase,
          // same muted colour — so the two stacked directly on top of each
          // other and the header read as a section with nothing in it. The
          // label was also wrong here on its own terms: this is the branch
          // where the work is NOT shared, so there is no split to describe.
          <p className="max-w-[68ch] whitespace-pre-line break-words text-sm leading-relaxed text-foreground">
            {scopeRationale}
          </p>
        ) : null}

        {anticipatedDocuments.length > 0 ? (
          <div
            className={
              isShared ? "space-y-2 border-t border-border pt-4" : "space-y-2"
            }
          >
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t(scopeItemsLabelKey)}
            </p>
            <ul className="space-y-2">
              {anticipatedDocuments.map((doc) => {
                // A slot the consumer left empty changes the estimate as much
                // as one they filled, so both are shown rather than only the
                // files that happen to exist.
                //
                // A text item is "provided" when a value came back, not when a
                // file did: it was never going to produce one, and reading it
                // off documentType would report every answered RUT as missing.
                const isText = doc.kind === "text";
                const answer = answerByLabel.get(doc.label) ?? null;
                const isProvided = isText
                  ? answer != null
                  : doc.documentType != null && provided.has(doc.documentType);
                // "adjuntado" is wrong for something that was typed, and
                // "todavía no adjuntado" tells a consumer to go find a file
                // for a box they were meant to type into.
                const statusKey = isText
                  ? isProvided
                    ? "requests.scope.valueProvided"
                    : "requests.scope.valueMissing"
                  : isProvided
                    ? "requests.scope.documentProvided"
                    : "requests.scope.documentMissing";
                return (
                  <li key={`${doc.label}-${doc.documentType ?? "none"}`} className="flex gap-2">
                    {isProvided ? (
                      <FileCheck2
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                    ) : (
                      <FileX2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    )}
                    <div className="min-w-0 max-w-[68ch] space-y-0.5">
                      <p className="text-sm text-foreground">
                        {doc.label}
                        <span className="text-muted-foreground">
                          {" · "}
                          {t(statusKey)}
                        </span>
                      </p>
                      {/* The answer itself, not just that one exists — it is
                          the whole reason the item was asked for. */}
                      {answer != null ? (
                        <p className="break-words text-sm font-medium text-foreground">{answer}</p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">{doc.why}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
